import { Action, createReducer, on } from "@ngrx/store";
import * as quotationsActions from './quotations.actions';
import { IQuotation, IQuotationDetail, IQuotationFilter, IQuotationKPI, IQuotationSourceContent, IStatusCount, IParseProduct, IOrderLineAlert, IHistory, ILineDeal, IUpdateLinesDelta, IUpdateLinesDeltaSummary, IProcessingCount, IDocumentCoordinatesResponse, IErpSyncStatus } from "../models/quotations.models";
import { IPagination } from "src/app/shared/models/page.model";
import { refillQuotationFilter } from "src/app/shared/utils/helpers";
export interface IFilterState {
  advanced: IQuotationFilter;
  keyword: string;
  isCopilot: boolean
}

export interface quotationsState {
   quotations: IQuotation[],
   kpi: IQuotationKPI | undefined
   status_count: IStatusCount | undefined
   quotation: IQuotationDetail | undefined
   quotationSourceContent: IQuotationSourceContent | undefined
   /** Set when source content was loaded; used to hydrate ``quotation.ai_details`` when GET omits it. */
   quotationSourceQuotationId: number | null
   /** WB-6815 document-coordinate index (hover highlights). Optional; keyed to the quotation below. */
   quotationDocumentCoordinates: IDocumentCoordinatesResponse | null
   quotationDocumentCoordinatesQuotationId: number | null
   metadata: IPagination | null
   linesMetadata: IPagination | null
   linesLoading: boolean
   /**
    * Set when a line request failed. `GetQuotationByIdError` leaves `linesMetadata`
    * alone, so `page < pages` stays true and anything that chains the next page off
    * `linesLoading` flipping false would re-dispatch immediately, forever. Consumers
    * of the background pagination chain gate on this; the manual "Load more" button
    * does not, so a user-driven retry still works and clears it on success.
    */
   linesLoadFailed: boolean
   filterState: IFilterState
   loading: boolean
   promptToOrderLoading: boolean
   alertsByLine: { [orderLineId: string]: IOrderLineAlert[] }
   hasAnyOpenAlerts: boolean
   fullHistory: IHistory[]
   historyLoading: boolean
   dealsByLine: { [orderLineId: string]: ILineDeal }
   orderDeals: { best_deal: any[]; best_free_shipping: any | null; best_buy_x_get_y: any | null; order_programs: any[] } | null
   showProcessing: boolean
   processing: IQuotation[]
   processing_count: IProcessingCount
}

export const initialquotationsState: quotationsState = {
  quotations: [],
  kpi: undefined,
  quotation: undefined,
  quotationSourceContent: undefined,
  quotationSourceQuotationId: null,
  quotationDocumentCoordinates: null,
  quotationDocumentCoordinatesQuotationId: null,
  status_count: undefined,
  filterState: {
    advanced: refillQuotationFilter(),
    keyword: '',
    isCopilot: false,

  },
  metadata: null,
  linesMetadata: null,
  linesLoading: false,
  linesLoadFailed: false,
  loading: false,
  promptToOrderLoading: false,
  alertsByLine: {},
  hasAnyOpenAlerts: false,
  fullHistory: [],
  historyLoading: false,
  dealsByLine: {},
  orderDeals: null,
  showProcessing: true,
  processing: [],
  processing_count: { processing: 0, needs_review: 0 },
}

function mergeQuotationAiFromSource(
  state: quotationsState,
  incoming: IQuotationDetail,
): IQuotationDetail {
  if (incoming.ai_details) {
    return incoming;
  }
  if (
    state.quotationSourceQuotationId === incoming.id
    && state.quotationSourceContent?.ai_details
  ) {
    return { ...incoming, ai_details: state.quotationSourceContent.ai_details };
  }
  if (state.quotation?.id === incoming.id && state.quotation.ai_details) {
    return { ...incoming, ai_details: state.quotation.ai_details };
  }
  return incoming;
}

/**
 * Patch the matching row of a quotations list array with the fresh counts
 * from a PATCH /lines `summary` (WB-8066). Used for BOTH `state.quotations`
 * and `state.processing` -- an order with unmatched lines (the side-by-side
 * panel's primary use case) is frequently sitting in `processing`
 * (needs_review), not `quotations`, so patching only one leaves the row
 * showing pre-match counts whenever it happens to be in the other bucket.
 */
function patchQuotationRow(rows: IQuotation[], orderId: number, summary: IUpdateLinesDeltaSummary): IQuotation[] {
  return rows.map((q) =>
    q.id === orderId
      ? {
          ...q,
          nbr_products: summary.nbr_products,
          total_skus: summary.total_skus,
          total_qty: summary.total_qty,
          amount_total: summary.amount_total,
          line_composition: summary.line_composition ?? q.line_composition,
        }
      : q
  );
}

const featureReducer = createReducer(
  initialquotationsState,
  on(
    quotationsActions.GetQuotationsSuccess,
    (state, action) => ({
      ...state,
      quotations: action.res.object?.quotations || state.quotations,
      kpi: action.res.object?.kpi,
      status_count: action.res.object?.status_count,
      processing: action.res.object?.processing ?? [],
      processing_count: action.res.object?.processing_count ?? { processing: 0, needs_review: 0 },
      metadata: action.res.metadata
    })
  ),
  on(
    quotationsActions.MarkQuotationRead,
    (state, action) => ({
      ...state,
      quotations: state.quotations.map((q) =>
        q.id === action.id ? { ...q, is_read: true } : q
      ),
    })
  ),
  on(
    quotationsActions.DismissDuplicateBadgeSuccess,
    (state, action) => ({
      ...state,
      quotations: state.quotations.map((q) =>
        q.id === action.id
          ? { ...q, is_duplicate_dismissed: true, duplicate_info: { has_duplicates: false, orders: [] } }
          : q
      ),
      quotation: state.quotation && state.quotation.id === action.id
        ? { ...state.quotation, is_duplicate_dismissed: true, duplicate_info: { has_duplicates: false, orders: [] } }
        : state.quotation,
    })
  ),
  on(
    quotationsActions.GetQuotationByIdSuccess,
    (state, action) => {
      const incoming = mergeQuotationAiFromSource(state, action.quotation);
      const incomingMeta = incoming.lines_metadata ?? null;
      if (action.append && state.quotation) {
        const existingIds = new Set(
          (state.quotation.products || []).map((p) => p.order_line_id)
        );
        const merged = [
          ...(state.quotation.products || []),
          ...(incoming.products || []).filter(
            (p) => !existingIds.has(p.order_line_id)
          ),
        ];
        return {
          ...state,
          quotation: {
            ...state.quotation,
            ...incoming,
            products: merged,
          },
          linesMetadata: incomingMeta,
          linesLoading: false,
          linesLoadFailed: false,
        };
      }
      return {
        ...state,
        quotation: incoming,
        linesMetadata: incomingMeta,
        linesLoading: false,
        linesLoadFailed: false,
      };
    }
  ),
  on(
    quotationsActions.AppendQuotationPage,
    (state) => ({
      ...state,
      linesLoading: true,
    })
  ),
  on(
    quotationsActions.GetQuotationByIdError,
    (state) => ({
      ...state,
      linesLoading: false,
      linesLoadFailed: true,
    })
  ),
  on(
    quotationsActions.UpdateQuotationLinesSuccess,
    (state, action) => {
      if (!state.quotation) return state;
      const { updated, added, removed, removed_unmatched, summary } = action.delta;
      const updatedById = new Map<number, IParseProduct>();
      (updated || []).forEach((p) => {
        if (p.order_line_id != null) updatedById.set(p.order_line_id, p);
      });
      const removedSet = new Set(removed || []);
      const matchedFromUnmatchedIds = new Set(
        (added || [])
          .map((p) => p.matched_from_unmatched_line_id)
          .filter((id): id is number => id != null)
      );
      const removedUnmatchedSet = new Set([
        ...(removed_unmatched || []),
        ...matchedFromUnmatchedIds,
      ]);

      const nextProducts = (state.quotation.products || [])
        .filter((p) => p.order_line_id == null || !removedSet.has(p.order_line_id))
        .map((p) => (p.order_line_id != null && updatedById.has(p.order_line_id)
          ? updatedById.get(p.order_line_id)!
          : p));
      const appended = (added || []).filter(
        (p) => !nextProducts.some(
          (existing) => existing.order_line_id != null && existing.order_line_id === p.order_line_id
        )
      );

      const aiDetails = state.quotation.ai_details;
      const nextAiDetails = aiDetails
        ? {
            ...aiDetails,
            unmatched_order_lines: (aiDetails.unmatched_order_lines || []).filter(
              (u) => !removedUnmatchedSet.has(u.id)
            ),
          }
        : aiDetails;

      return {
        ...state,
        quotation: {
          ...state.quotation,
          products: [...nextProducts, ...appended],
          nbr_products: summary.nbr_products,
          total_skus: summary.total_skus,
          total_order_lines: summary.total_order_lines,
          total_qty: summary.total_qty,
          amount_total: summary.amount_total,
          cost_total: summary.cost_total,
          margin_total: summary.margin_total,
          lines_metadata: summary.lines_metadata,
          // Keep in sync with every line update (WB-7024) -- otherwise these
          // go stale after a price edit until the next full refetch.
          priced_lines_total: summary.priced_lines_total ?? state.quotation.priced_lines_total,
          all_lines_no_price: summary.all_lines_no_price ?? state.quotation.all_lines_no_price,
          // WB-8066: without this, matching/unmatching a line here (the AI
          // review flow's primary action) left the "Order lines" breakdown
          // showing the pre-match unmatched count until a full refetch.
          line_composition: summary.line_composition ?? state.quotation.line_composition,
          ai_details: nextAiDetails,
        },
        // WB-8066: the list row for this order (behind the panel, or the row the
        // panel was opened from) otherwise keeps showing the pre-match counts
        // until the list is manually refetched -- match state.quotation above.
        // Orders with unmatched lines are frequently sitting in `processing`
        // (needs_review), not `quotations` -- that's exactly the bucket the
        // panel's primary use case lives in, so both must be patched.
        quotations: patchQuotationRow(state.quotations, action.orderId, summary),
        processing: patchQuotationRow(state.processing, action.orderId, summary),
        linesMetadata: summary.lines_metadata,
      };
    }
  ),
  on(
    quotationsActions.UpdateQuotationDetailsSuccess,
    (state, action) => {
      console.log('UpdateQuotationDetailsSuccess', action.changes);
      if (!state.quotation) return state;
      return {
        ...state,
        quotation: {
          ...state.quotation,
          ...action.changes,
          products: state.quotation.products,
          lines_metadata: state.quotation.lines_metadata,
          ai_details: state.quotation.ai_details,
        },
      };
    }
  ),
  on(
    quotationsActions.DismissSuggestedLocationSuccess,
    (state, action) => {
      if (!state.quotation || state.quotation.id !== action.id) return state;
      const dismissFlag = { suggested_shipping_location_dismiss: true };
      const sourceMatches =
        state.quotationSourceQuotationId === action.id && !!state.quotationSourceContent?.ai_details;
      return {
        ...state,
        quotation: {
          ...state.quotation,
          ai_details: state.quotation.ai_details
            ? { ...state.quotation.ai_details, ...dismissFlag }
            : state.quotation.ai_details,
        },
        quotationSourceContent: sourceMatches
          ? {
              ...state.quotationSourceContent!,
              ai_details: { ...state.quotationSourceContent!.ai_details!, ...dismissFlag },
            }
          : state.quotationSourceContent,
      };
    }
  ),
  on(
    quotationsActions.GetQuotationSourceContentSuccess,
    (state, action) => {
      const next: quotationsState = {
        ...state,
        quotationSourceContent: action.quotation,
        quotationSourceQuotationId: action.quotationId,
      };
      if (
        state.quotation?.id === action.quotationId
        && action.quotation.ai_details
      ) {
        return {
          ...next,
          quotation: {
            ...state.quotation,
            ai_details: action.quotation.ai_details,
          },
        };
      }
      return next;
    }
  ),
  on(
    quotationsActions.GetQuotationDocumentCoordinatesSuccess,
    (state, action) => ({
      ...state,
      quotationDocumentCoordinates: action.coordinates,
      quotationDocumentCoordinatesQuotationId: action.quotationId,
    })
  ),
  on(
    quotationsActions.clearQuotations,
    (state, action) => ({
      ...state,
      quotation: undefined,
      quotationSourceContent: undefined,
      quotationSourceQuotationId: null,
      quotationDocumentCoordinates: null,
      quotationDocumentCoordinatesQuotationId: null,
      linesMetadata: null,
      linesLoadFailed: false,
      linesLoading: false,
      alertsByLine: {},
      hasAnyOpenAlerts: false,
      fullHistory: [],
      historyLoading: false,
      dealsByLine: {},
      orderDeals: null,
    })
  ),
  on(
    quotationsActions.SetIsCopilot,
    (state, action) => ({
      ...state,
      filterState: {
        ...state.filterState,
        isCopilot: action.isCopilot
      }
    })
  ),
  on(
    quotationsActions.SetQuotationsLoader,
    (state, action) => ({
      ...state,
      loading: action.isLoading
    })
  ),
  on(
    quotationsActions.SetQuotationFilter,
    (state, action) => ({
      ...state,
      filterState: {
        ...state.filterState,
        advanced: action.advanced!,
        keyword: action.keyword,
    }
    })
  ),
  on(
    quotationsActions.SetQuotationSearch,
    (state, action) => ({
      ...state,
      filterState: {
        ...state.filterState,
        searchQuotation: action.search
      }
    })
  ),
  on(
    quotationsActions.ClearQuotationSourceContent,
    (state, action) => ({
      ...state,
      quotationSourceContent: undefined,
      quotationSourceQuotationId: null,
      quotationDocumentCoordinates: null,
      quotationDocumentCoordinatesQuotationId: null,
    })
  ),
  on(
    quotationsActions.SetPromptToOrderLoader,
    (state, action) => ({
      ...state,
      promptToOrderLoading: action.isLoading
    })
  ),
  on(
    quotationsActions.LoadQuotationAlertsSuccess,
    (state, action) => ({
      ...state,
      alertsByLine: action.alertsResponse.alerts_by_line,
      hasAnyOpenAlerts: action.alertsResponse.has_any_open,
    })
  ),
  on(
    quotationsActions.ToggleOrderLineAlertSuccess,
    (state, action) => {
      const lineId = String(action.orderLineId);
      const lineAlerts = (state.alertsByLine[lineId] || []).map(a =>
        a.id === action.alert.id ? { ...action.alert } : a
      );
      const newAlertsByLine = { ...state.alertsByLine, [lineId]: lineAlerts };
      const hasAnyOpen = Object.values(newAlertsByLine).some(
        alerts => alerts.some(a => a.state === 'open')
      );
      return {
        ...state,
        alertsByLine: newAlertsByLine,
        hasAnyOpenAlerts: hasAnyOpen,
      };
    }
  ),
  on(
    quotationsActions.IgnoreAllAlertsSuccess,
    (state, action) => {
      const newAlertsByLine: { [key: string]: IOrderLineAlert[] } = {};
      for (const [lineId, alerts] of Object.entries(state.alertsByLine)) {
        newAlertsByLine[lineId] = alerts.map(a =>
          action.alertIds.includes(a.id) ? { ...a, state: 'ignored' } : a
        );
      }
      const hasAnyOpen = Object.values(newAlertsByLine).some(
        alerts => alerts.some(a => a.state === 'open')
      );
      return {
        ...state,
        alertsByLine: newAlertsByLine,
        hasAnyOpenAlerts: hasAnyOpen,
      };
    }
  ),
  on(
    quotationsActions.LoadQuotationHistory,
    (state) => ({
      ...state,
      historyLoading: true,
    })
  ),
  on(
    quotationsActions.LoadQuotationHistorySuccess,
    (state, action) => ({
      ...state,
      fullHistory: action.history,
      historyLoading: false,
    })
  ),
  on(
    quotationsActions.LoadQuotationHistoryError,
    (state) => ({
      ...state,
      historyLoading: false,
    })
  ),
  on(
    quotationsActions.LoadQuotationDealsSuccess,
    (state, action) => ({
      ...state,
      dealsByLine: action.dealsResponse.deals_by_line,
      orderDeals: action.dealsResponse.order_deals,
    })
  ),
  on(
    quotationsActions.ArchiveQuotationSuccess,
    (state, action) => {
      const nextStatusCount = state.status_count
        ? {
            ...state.status_count,
            archived: (state.status_count.archived ?? 0) + 1,
            all: Math.max(0, (state.status_count.all ?? 0) - 1),
          }
        : state.status_count;
      const nextDetail = state.quotation && state.quotation.id === action.id
        ? { ...state.quotation, ...(action.quotation ?? {}), is_archived: true, archived_at: action.quotation?.archived_at ?? null }
        : state.quotation;
      return {
        ...state,
        quotations: state.quotations.filter((q) => q.id !== action.id),
        status_count: nextStatusCount,
        quotation: nextDetail,
      };
    }
  ),
  on(
    quotationsActions.UnarchiveQuotationSuccess,
    (state, action) => {
      const nextStatusCount = state.status_count
        ? {
            ...state.status_count,
            archived: Math.max(0, (state.status_count.archived ?? 0) - 1),
            all: (state.status_count.all ?? 0) + 1,
          }
        : state.status_count;
      const nextDetail = state.quotation && state.quotation.id === action.id
        ? { ...state.quotation, ...(action.quotation ?? {}), is_archived: false, archived_at: null }
        : state.quotation;
      return {
        ...state,
        status_count: nextStatusCount,
        quotation: nextDetail,
      };
    }
  ),
  on(
    quotationsActions.deleteQuotationSuccess,
    (state, action) => {
      const removed = state.processing.find((q) => q.id === action.id);
      const processing_count = removed
        ? {
            processing: Math.max(0, state.processing_count.processing - (removed.ato_processing_status === 'processing' ? 1 : 0)),
            needs_review: Math.max(0, state.processing_count.needs_review - (removed.ato_processing_status === 'needs_review' ? 1 : 0)),
          }
        : state.processing_count;
      return {
        ...state,
        quotations: state.quotations.filter((q) => q.id !== action.id),
        processing: state.processing.filter((q) => q.id !== action.id),
        processing_count,
      };
    }
  ),
  on(
    quotationsActions.ToggleProcessingVisibility,
    (state) => ({
      ...state,
      showProcessing: !state.showProcessing,
    })
  ),
  on(
    quotationsActions.SetProcessingVisibility,
    (state, action) => ({
      ...state,
      showProcessing: action.visible,
    })
  ),
  on(
    quotationsActions.SetQuotationOwner,
    (state, action) => ({
      ...state,
      quotations: state.quotations.map((q) =>
        q.id === action.id ? { ...q, owner: action.owner } : q
      ),
      quotation: state.quotation && state.quotation.id === action.id
        ? { ...state.quotation, owner: action.owner }
        : state.quotation,
    })
  ),
  on(
    quotationsActions.ErpSyncStatusChanged,
    (state, action) => {
      const patch = {
        sync_status: action.sync_status,
        error_type: action.error_type,
        error: action.error,
        erp_message: action.erp_message,
      };
      return {
        ...state,
        quotations: state.quotations.map((q) =>
          q.id === action.id ? { ...q, erp_sync: { ...q.erp_sync, ...patch } as IErpSyncStatus } : q
        ),
        quotation: state.quotation && state.quotation.id === action.id
          ? { ...state.quotation, erp_sync: { ...state.quotation.erp_sync, ...patch } as IErpSyncStatus }
          : state.quotation,
      };
    }
  ),
)

export function quotationsReducer(state: quotationsState | undefined, action: Action) {
  return featureReducer(state, action);
}
